/**
 * Auth Slice
 * Manages authentication state
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import type { AuthState } from '../types';
import type { AdminUser } from '../../types/api.types';

// Initial state
const initialState: AuthState = {
    user: null,
    token: null,
    isAuthenticated: false,
    loading: 'idle',
    error: null,
};

// ============================================
// Async Thunks
// ============================================

/**
 * Load auth data from localStorage on app initialization
 */
export const loadAuthFromStorage = createAsyncThunk('auth/loadFromStorage', async () => {
    const token = localStorage.getItem('authToken');
    const userJson = localStorage.getItem('authUser');

    if (token && userJson) {
        const user: AdminUser = JSON.parse(userJson);
        return { user, token };
    }

    return null;
});

// ============================================
// Slice
// ============================================

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        /**
         * Set authentication data after successful login
         */
        setAuth: (state, action: { payload: { user: AdminUser; token: string }; type: string }) => {
            state.user = action.payload.user;
            state.token = action.payload.token;
            state.isAuthenticated = true;
            state.error = null;

            // Persist to localStorage
            localStorage.setItem('authToken', action.payload.token);
            localStorage.setItem('authUser', JSON.stringify(action.payload.user));
        },

        /**
         * Clear authentication data on logout
         */
        clearAuth: (state) => {
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            state.error = null;

            // Clear from localStorage
            localStorage.removeItem('authToken');
            localStorage.removeItem('authUser');
        },

        /**
         * Set error
         */
        setAuthError: (state, action: { payload: string; type: string }) => {
            state.error = action.payload;
        },

        /**
         * Clear error
         */
        clearAuthError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Load from storage
            .addCase(loadAuthFromStorage.pending, (state) => {
                state.loading = 'loading';
            })
            .addCase(loadAuthFromStorage.fulfilled, (state, action) => {
                state.loading = 'idle';
                if (action.payload) {
                    state.user = action.payload.user;
                    state.token = action.payload.token;
                    state.isAuthenticated = true;
                } else {
                    state.isAuthenticated = false;
                }
            })
            .addCase(loadAuthFromStorage.rejected, (state) => {
                state.loading = 'idle';
                state.isAuthenticated = false;
            });
    },
});

// ============================================
// Actions
// ============================================

export const { setAuth, clearAuth, setAuthError, clearAuthError } = authSlice.actions;

// ============================================
// Selectors (Custom Hooks)
// ============================================

/**
 * Get entire auth state
 */
export const useAuthState = () => {
    return useSelector((state: RootState) => state.auth);
};

/**
 * Get authenticated user
 */
export const useAuthUser = () => {
    return useSelector((state: RootState) => state.auth.user);
};

/**
 * Get auth token
 */
export const useAuthToken = () => {
    return useSelector((state: RootState) => state.auth.token);
};

/**
 * Check if user is authenticated
 */
export const useIsAuthenticated = () => {
    return useSelector((state: RootState) => state.auth.isAuthenticated);
};

/**
 * Get auth loading state
 */
export const useAuthLoading = () => {
    return useSelector((state: RootState) => state.auth.loading);
};

/**
 * Get auth error
 */
export const useAuthError = () => {
    return useSelector((state: RootState) => state.auth.error);
};

// ============================================
// Reducer
// ============================================

export default authSlice.reducer;
